import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { adminApi } from "../services/api";
import MultiSelectDropdown from "../components/common/MultiSelectDropdown";
import Table from "../components/common/Table";
import TableLoader from "../components/common/TableLoader";
import Tooltip from "../components/common/Tooltip";

const AVATAR_COLORS = ["#3a47ff", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4", "#ef4444", "#0a1530"];
const avatarColor = (s) => AVATAR_COLORS[(s || "").charCodeAt(0) % AVATAR_COLORS.length];
const initials = (n) =>
  (n || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const AuditLogsByEndpointPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEndpoint = searchParams.get("selected");

  const [endpoints, setEndpoints] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [methodFilter, setMethodFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    methods: [],
    responseStatuses: [],
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [endpointInfo, setEndpointInfo] = useState(null);
  const [stats, setStats] = useState({
    totalEndpoints: 0,
    totalLogs: 0,
  });

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    if (selectedEndpoint) {
      fetchLogsByEndpoint();
    } else {
      fetchEndpointsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEndpoint, pagination.page, pagination.limit, query, methodFilter, statusFilter]);

  const fetchFilterOptions = async () => {
    try {
      const response = await adminApi.getAuditLogFilters();

      setFilterOptions((prev) => ({ ...prev, ...response.data }));
    } catch (err) {
      console.error("Failed to load filter options", err);
    }
  };

  const fetchEndpointsList = async () => {
    const isFirstLoad = loading && endpoints.length === 0;
    if (isFirstLoad) {
      setLoading(true);
    } else {
      setIsRefetching(true);
    }
    try {
      const response = await adminApi.getEndpointsList({
        page: pagination.page,
        limit: pagination.limit,
        search: query,
        method: methodFilter,
        responseStatus: statusFilter,
      });
      setEndpoints(response.data.endpoints);
      setPagination((prev) => ({
        ...prev,
        ...response.data.pagination,
      }));
      setStats({
        totalEndpoints: response.data.pagination.total,
        totalLogs: response.data.endpoints.reduce((sum, e) => sum + e.logCount, 0),
      });
    } catch (err) {
      setError("Failed to load endpoints list");
      console.error(err);
    } finally {
      setLoading(false);
      setIsRefetching(false);
    }
  };

  const fetchLogsByEndpoint = async () => {
    const isFirstLoad = loading && logs.length === 0;
    if (isFirstLoad) {
      setLoading(true);
    } else {
      setIsRefetching(true);
    }
    try {
      const response = await adminApi.getLogsByEndpoint(selectedEndpoint, {
        page: pagination.page,
        limit: pagination.limit,
        method: methodFilter,
        responseStatus: statusFilter,
      });
      setLogs(response.data.logs);
      setEndpointInfo(response.data.endpointInfo);
      setPagination((prev) => ({
        ...prev,
        ...response.data.pagination,
      }));
    } catch (err) {
      setError("Failed to load endpoint logs");
      console.error(err);
    } finally {
      setLoading(false);
      setIsRefetching(false);
    }
  };

  const handleViewLogs = (endpoint) => {
    setSearchParams({ selected: endpoint.name });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleBackToList = () => {
    // Preserve navigation state if it exists from the previous page
    if (location.state?.filters) {
      navigate('/audit-logs', { state: location.state });
    } else {
      navigate('/audit-logs');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, limit: newPageSize, page: 1 }));
  };

  const handleReset = () => {
    setQuery("");
    setMethodFilter([]);
    setStatusFilter([]);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Endpoints list columns
  const endpointsColumns = [
    {
      key: "endpoint",
      label: "Endpoint",
      render: (e) => (
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-indigo/10 grid place-items-center shrink-0">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#3a47ff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-ink text-[13px] truncate">
              {e.name || "Unknown"}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "logCount",
      label: "Logs",
      render: (e) => (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-indigo/10 text-indigo text-xs font-semibold">
          {e.logCount}
        </span>
      ),
    },
    {
      key: "lastActivity",
      label: "Last Activity",
      render: (e) => (
        <span className="text-muted text-xs">
          {new Date(e.lastActivity).toLocaleString()}
        </span>
      ),
    },
    {
      key: "action",
      label: "Action",
      render: (e) => (
        <button
          onClick={() => handleViewLogs(e)}
          className="py-1.5 px-3 border border-line rounded-lg bg-white text-[13px] font-medium hover:bg-soft transition-colors"
        >
          View
        </button>
      ),
    },
  ];

  // Logs columns (same as AuditLogsPage)
  const logsColumns = [
    {
      key: "user",
      label: "User",
      render: (log) => (
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-full grid place-items-center text-white font-semibold text-[10px] shrink-0"
            style={{ background: avatarColor(log.userEmail) }}
          >
            {initials(log.userEmail)}
          </div>
          <Tooltip content={log.userEmail}>
            <span className="text-xs text-ink truncate max-w-[120px] block">
              {log.userEmail}
            </span>
          </Tooltip>
        </div>
      ),
    },
    {
      key: "endpoint",
      label: "API Endpoint",
      render: (log) => (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${log.method === "GET"
                  ? "bg-blue-100 text-blue-800"
                  : log.method === "POST"
                    ? "bg-green-100 text-green-800"
                    : log.method === "PUT"
                      ? "bg-yellow-100 text-yellow-800"
                      : log.method === "DELETE"
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-100 text-gray-800"
                }`}
            >
              {log.method}
            </span>
            <Tooltip content={log.endpoint}>
              <span className="text-xs text-ink font-mono truncate max-w-[200px] block">
                {log.endpoint}
              </span>
            </Tooltip>
          </div>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (log) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${log.status >= 200 && log.status < 300
              ? "bg-green-100 text-green-800"
              : log.status >= 400
                ? "bg-red-100 text-red-800"
                : "bg-yellow-100 text-yellow-800"
            }`}
        >
          {log.status}
        </span>
      ),
    },
    {
      key: "timestamp",
      label: "Timestamp",
      render: (log) => (
        <span className="text-xs text-muted whitespace-nowrap">
          {new Date(log.timestamp).toLocaleString()}
        </span>
      ),
    },
    {
      key: "duration",
      label: "Duration",
      render: (log) => (
        <span className="text-xs text-muted">{log.duration || 0}ms</span>
      ),
    },
  ];

  if (selectedEndpoint) {
    // Detail view - showing logs for selected endpoint
    return (
      <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
        <div className="flex items-center gap-2 mb-6 text-sm">
          <button
            onClick={handleBackToList}
            className="text-indigo hover:underline font-medium"
          >
            ← All Endpoints
          </button>
          <span className="text-muted">›</span>
          <span className="text-ink font-medium">{endpointInfo?.name || selectedEndpoint}</span>
        </div>

        {endpointInfo && (
          <div className="border border-line rounded-xl p-5 bg-soft mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-indigo/10 grid place-items-center">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#3a47ff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
              </div>
              <div>
                <div className="font-bold text-lg text-ink">{endpointInfo.name}</div>
                <div className="text-muted text-sm">
                  {pagination.total} total logs
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-3 items-center flex-wrap mb-5">
          <MultiSelectDropdown
            options={filterOptions.methods.map((m) => ({ label: m, value: m }))}
            value={methodFilter}
            onSelect={setMethodFilter}
            buttonLabel="Method"
          />
          <MultiSelectDropdown
            options={filterOptions.responseStatuses.map((s) => ({ label: s?.toUpperCase(), value: s }))}
            value={statusFilter}
            onSelect={setStatusFilter}
            buttonLabel="Status"
          />
          <button
            onClick={handleReset}
            className="py-2.5 px-3.5 border border-line rounded-lg bg-white text-[13px] font-medium text-muted hover:bg-soft transition-all"
          >
            Reset
          </button>
        </div>

        <Table
          columns={logsColumns}
          data={logs}
          loading={loading}
          error={error}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          maxHeight="calc(100vh - 320px)"
          emptyState={
            <tr>
              <td colSpan={logsColumns.length} className="py-20 px-4">
                <div className="flex flex-col items-center justify-center text-center">
                  <h3 className="text-lg font-semibold text-ink mb-2">No Logs Found</h3>
                  <p className="text-sm text-muted max-w-md">
                    No logs found for this endpoint with the current filters.
                  </p>
                </div>
              </td>
            </tr>
          }
        />
      </div>
    );
  }

  // List view - showing all endpoints
  return (
    <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight m-0">Audit Logs by Endpoint</h1>
        <p className="text-muted text-[15px] mt-2 mb-0">
          Browse and filter audit logs grouped by endpoints
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">
            Total Endpoints
          </div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight">{stats.totalEndpoints}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">
            Total Logs
          </div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight">{stats.totalLogs}</div>
        </div>
      </div>

      <div className="flex gap-3 items-center flex-wrap mb-5">
        <div className="flex-1 min-w-[240px] relative">
          <svg
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            type="text"
            placeholder="Search by endpoint…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full py-2.5 px-3.5 pl-10 border border-line rounded-lg text-sm bg-soft transition-all focus:outline-none focus:border-indigo focus:bg-white focus:ring-3 focus:ring-indigo/10"
          />
        </div>
        <MultiSelectDropdown
          options={filterOptions.methods.map((m) => ({ label: m, value: m }))}
          value={methodFilter}
          onSelect={setMethodFilter}
          buttonLabel="Method"
        />
        <MultiSelectDropdown
          options={filterOptions.responseStatuses.map((s) => ({ label: s?.toUpperCase(), value: s }))}
          value={statusFilter}
          onSelect={setStatusFilter}
          buttonLabel="Status"
        />
        <button
          onClick={handleReset}
          className="py-2.5 px-3.5 border border-line rounded-lg bg-white text-[13px] font-medium text-muted hover:bg-soft transition-all"
        >
          Reset
        </button>
      </div>

      <div className="relative">
        <Table
          columns={endpointsColumns}
          data={endpoints}
          loading={loading}
          error={error}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          maxHeight="calc(100vh - 330px)"
          emptyState={
            <tr>
              <td colSpan={endpointsColumns.length} className="py-20 px-4">
                <div className="flex flex-col items-center justify-center text-center">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mb-4"
                  >
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                  <h3 className="text-lg font-semibold text-ink mb-2">No Endpoints Found</h3>
                  <p className="text-sm text-muted max-w-md">
                    {query || methodFilter.length || statusFilter.length
                      ? "No endpoints match your current filters. Try adjusting your search criteria."
                      : "No endpoints have generated audit logs yet."}
                  </p>
                </div>
              </td>
            </tr>
          }
        />
        {isRefetching && <TableLoader overlay={true} message="Loading..." />}
      </div>
    </div>
  );
};

export default AuditLogsByEndpointPage;
